/* eslint-disable */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import '../../components/skeletonRow.css';
import './AdsReport.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faChevronLeft,
  faChevronRight,
  faDownload,
  faSync,
  faCalendarAlt,
  faBullhorn,
  faCloudUploadAlt,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { API_URL } from '../../config';
import { showToast } from '../../components/toast';
import { PermissionWrapper } from '../../helpers/permission';
import AdsReportStats from './AdsReportStats';
import AdsReportTable from './AdsReportTable';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const defaultDateRange = {
  startDate: moment().subtract(29, 'days').format('YYYY-MM-DD'),
  endDate: moment().format('YYYY-MM-DD'),
};

const formatShortRupee = (value) => {
  if (value === 0 || value === '0') return '₹0';
  const val = Number(value);
  if (isNaN(val)) return '₹0';
  const absVal = Math.abs(val);
  const sign = val < 0 ? '-' : '';
  if (absVal >= 10000000) {
    const cr = val / 10000000;
    return `${sign}₹${cr.toFixed(1).replace(/\.0$/, '')} Cr`;
  } else if (absVal >= 100000) {
    const lk = val / 100000;
    return `${sign}₹${lk.toFixed(1).replace(/\.0$/, '')} L`;
  } else if (absVal >= 1000) {
    const k = val / 1000;
    return `${sign}₹${k.toFixed(1).replace(/\.0$/, '')} K`;
  }
  return `₹${val.toLocaleString('en-IN')}`;
};

const AdsReportDetail = () => {
  const { platform, campaignName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const isCampaignMode = Boolean(campaignName) || location.pathname.includes('/adsReport/campaign/');
  const rawName = isCampaignMode ? (campaignName ? decodeURIComponent(campaignName) : '') : (platform || '');
  const currentTitle = rawName || (isCampaignMode ? 'Unmapped' : 'Platform');

  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Date Range Filter State
  const [dateRange, setDateRange] = useState(defaultDateRange);

  // Column Filters for Platform/Campaign Detail Table
  const [columnFilters, setColumnFilters] = useState({
    platform: '',
    campaign: '',
    campaign_name: '',
    month: '',
    cost: '',
    leads: '',
    cpl: '',
    likely: '',
    sales: '',
    revenue: '',
  });

  // Dropdown options
  const [filterOptions, setFilterOptions] = useState({
    platforms: [],
    campaigns: [],
    campaign_names: [],
    months: [],
  });

  // Summary & Chart Data for this platform / campaign
  const [summaryData, setSummaryData] = useState({ kpi: {}, charts: {} });

  // Detailed Table Data
  const [tableData, setTableData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [orderBy, setOrderBy] = useState('date');
  const [orderDir, setOrderDir] = useState('DESC');

  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await axios.get(`${API_URL}report-ads/filter-options`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) {
        setFilterOptions(res.data.data);
      }
    } catch (err) {
      console.error('Failed to load filter options', err);
    }
  }, []);

  // Fetch Summary Data (filtered specifically for this platform or campaign)
  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const payload = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        platform: isCampaignMode ? (columnFilters.platform || undefined) : (platform || undefined),
        campaign_name: isCampaignMode ? rawName : (columnFilters.campaign_name || undefined),
        campaign: columnFilters.campaign || undefined,
        month: columnFilters.month || undefined,
        cost: columnFilters.cost || undefined,
        leads: columnFilters.leads || undefined,
        cpl: columnFilters.cpl || undefined,
        likely: columnFilters.likely || undefined,
        sales: columnFilters.sales || undefined,
        revenue: columnFilters.revenue || undefined,
      };

      const res = await axios.post(`${API_URL}report-ads/summary`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data?.success) {
        setSummaryData(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch summary data', err);
      showToast.error(`Failed to load ${currentTitle} analytics.`);
    } finally {
      setLoading(false);
    }
  }, [platform, campaignName, isCampaignMode, rawName, currentTitle, dateRange, columnFilters]);

  // Fetch Detailed Table Data for this platform or campaign
  const fetchTable = useCallback(
    async (
      page = currentPage,
      limit = pageSize,
      sortCol = orderBy,
      sortDir = orderDir,
      filters = columnFilters
    ) => {
      try {
        setTableLoading(true);
        const token = localStorage.getItem('authToken');
        const payload = {
          startDate: dateRange.startDate || undefined,
          endDate: dateRange.endDate || undefined,
          platform: isCampaignMode ? (filters.platform || undefined) : (platform || undefined),
          campaign_name: isCampaignMode ? rawName : (filters.campaign_name || undefined),
          campaign: filters.campaign || undefined,
          month: filters.month || undefined,
          cost: filters.cost || undefined,
          leads: filters.leads || undefined,
          cpl: filters.cpl || undefined,
          likely: filters.likely || undefined,
          sales: filters.sales || undefined,
          revenue: filters.revenue || undefined,
          page,
          limit,
          orderBy: sortCol,
          orderDir: sortDir,
        };

        const res = await axios.post(`${API_URL}report-ads/table`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data?.success) {
          setTableData(res.data.data.list || []);
          setTotalRecords(res.data.data.total || 0);
          setCurrentPage(page);
        }
      } catch (err) {
        console.error('Failed to fetch table data', err);
      } finally {
        setTableLoading(false);
      }
    },
    [platform, campaignName, isCampaignMode, rawName, dateRange, columnFilters, currentPage, pageSize, orderBy, orderDir]
  );

  // Scroll to top on mount / platform / campaign change
  useEffect(() => {
    window.scrollTo(0, 0);
    const contentEl = document.getElementById('content');
    if (contentEl) contentEl.scrollTop = 0;
    const pageContainer = document.getElementById('page-container');
    if (pageContainer) pageContainer.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [platform, campaignName]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchSummary();
    fetchTable(1, pageSize, orderBy, orderDir, columnFilters);
  }, [fetchSummary, dateRange]);

  // Quick Date Preset Handler
  const handleDatePreset = (preset) => {
    if (preset === 'today') {
      const today = moment().format('YYYY-MM-DD');
      setDateRange({ startDate: today, endDate: today });
    } else if (preset === 'yesterday') {
      const yesterday = moment().subtract(1, 'days').format('YYYY-MM-DD');
      setDateRange({ startDate: yesterday, endDate: yesterday });
    } else if (preset === '7days') {
      setDateRange({
        startDate: moment().subtract(6, 'days').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
      });
    } else if (preset === '30days') {
      setDateRange(defaultDateRange);
    } else if (preset === 'thisMonth') {
      setDateRange({
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        endDate: moment().endOf('month').format('YYYY-MM-DD'),
      });
    } else if (preset === 'all') {
      setDateRange({ startDate: '', endDate: '' });
    }
  };

  const hasAnyFilter = () => {
    return (
      columnFilters.campaign !== '' ||
      columnFilters.month !== '' ||
      columnFilters.cost !== '' ||
      columnFilters.leads !== '' ||
      columnFilters.cpl !== '' ||
      columnFilters.likely !== '' ||
      columnFilters.sales !== '' ||
      columnFilters.revenue !== '' ||
      dateRange.startDate !== defaultDateRange.startDate ||
      dateRange.endDate !== defaultDateRange.endDate
    );
  };

  const handleClearFilters = () => {
    setDateRange(defaultDateRange);
    const emptyFilters = {
      platform: '',
      campaign: '',
      campaign_name: '',
      month: '',
      cost: '',
      leads: '',
      cpl: '',
      likely: '',
      sales: '',
      revenue: '',
    };
    setColumnFilters(emptyFilters);
    fetchTable(1, pageSize, orderBy, orderDir, emptyFilters);
  };

  // Export CSV for this platform or campaign
  const handleExportCSV = async () => {
    try {
      setExportLoading(true);
      const token = localStorage.getItem('authToken');
      const payload = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        platform: isCampaignMode ? (columnFilters.platform || undefined) : (platform || undefined),
        campaign_name: isCampaignMode ? rawName : (columnFilters.campaign_name || undefined),
        campaign: columnFilters.campaign || undefined,
        month: columnFilters.month || undefined,
        cost: columnFilters.cost || undefined,
        leads: columnFilters.leads || undefined,
        cpl: columnFilters.cpl || undefined,
        likely: columnFilters.likely || undefined,
        sales: columnFilters.sales || undefined,
        revenue: columnFilters.revenue || undefined,
      };

      const res = await axios.post(`${API_URL}report-ads/export`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const safePrefix = (currentTitle || 'report').replace(/[^a-zA-Z0-9_-]/g, '_');
      link.setAttribute('download', `${safePrefix}_ads_report_${moment().format('YYYYMMDD_HHmm')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast.success(`${currentTitle} ads report exported successfully!`);
    } catch (err) {
      console.error('Export error', err);
      showToast.error(`Failed to export ${currentTitle} report.`);
    } finally {
      setExportLoading(false);
    }
  };

  const refreshAllData = () => {
    fetchFilterOptions();
    fetchSummary();
    fetchTable(currentPage, pageSize, orderBy, orderDir, columnFilters);
  };

  // Recalculate Ads Report metrics from CRM
  const handleRecalculate = async () => {
    try {
      setRecalculating(true);
      const token = localStorage.getItem('authToken');
      const res = await axios.post(
        `${API_URL}backendApi/recalculate-ads-report`,
        {},
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );

      if (res.data?.success) {
        showToast.success(res.data.message || 'Ads report recalculated successfully!');
        refreshAllData();
      } else {
        showToast.error(res.data?.message || 'Failed to recalculate Ads report.');
      }
    } catch (err) {
      console.error('Recalculate error', err);
      showToast.error(err.response?.data?.message || 'Failed to recalculate Ads report.');
    } finally {
      setRecalculating(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, totalRecords);
  const showingRecords = `Showing ${startRecord} to ${endRecord} of ${totalRecords.toLocaleString()} entries`;

  // Chart 1: Platform Trend Data
  const trends = summaryData.charts?.trends || [];
  const trendLabels = trends.map((t) => t.date || t.month);
  const trendCostData = trends.map((t) => t.cost);
  const trendLeadsData = trends.map((t) => t.leads);

  const trendChartData = {
    labels: trendLabels.length > 0 ? trendLabels : ['No Data'],
    datasets: [
      {
        type: 'bar',
        label: 'Ad Spend (₹)',
        data: trendCostData.length > 0 ? trendCostData : [0],
        backgroundColor: 'rgba(59, 130, 246, 0.75)',
        borderColor: '#2563eb',
        borderWidth: 1,
        borderRadius: 4,
        yAxisID: 'yCost',
        order: 2,
      },
      {
        type: 'line',
        label: 'Leads',
        data: trendLeadsData.length > 0 ? trendLeadsData : [0],
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        borderWidth: 2.5,
        tension: 0.3,
        fill: true,
        pointRadius: 3,
        pointHoverRadius: 6,
        pointBackgroundColor: '#10b981',
        yAxisID: 'yLeads',
        order: 1,
      },
    ],
  };

  const trendChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top', labels: { boxWidth: 12, font: { size: 12 } } },
      tooltip: {
        callbacks: {
          label: (context) => {
            if (context.dataset.yAxisID === 'yCost') {
              return `Ad Spend: ₹${Number(context.raw).toLocaleString('en-IN')}`;
            }
            return `Leads: ${Number(context.raw).toLocaleString('en-IN')}`;
          },
        },
      },
    },
    scales: {
      x: { grid: { display: false } },
      yCost: {
        type: 'linear',
        position: 'left',
        title: { display: true, text: 'Spend (₹)' },
        ticks: { callback: (v) => formatShortRupee(v) },
        grid: { color: '#f1f5f9' },
      },
      yLeads: {
        type: 'linear',
        position: 'right',
        title: { display: true, text: 'Leads' },
        grid: { display: false },
        ticks: { callback: (v) => Number(v).toLocaleString('en-IN') },
      },
    },
  };

  // Chart 2: Top Campaigns by Spend for this platform
  const topCampaigns = summaryData.charts?.topCampaigns || [];
  const campaignLabels = topCampaigns.map((c) => {
    const name = c.campaign_name || c.campaign || '';
    return name.length > 55 ? `${name.substring(0, 52)}...` : name;
  });
  const campaignSpendData = topCampaigns.map((c) => c.cost);

  const topCampaignsData = {
    labels: campaignLabels.length > 0 ? campaignLabels : ['No Campaigns'],
    datasets: [
      {
        label: 'Ad Spend (₹)',
        data: campaignSpendData.length > 0 ? campaignSpendData : [0],
        backgroundColor: 'rgba(99, 102, 241, 0.8)',
        borderColor: '#6366f1',
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const topCampaignsOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            const idx = items[0].dataIndex;
            return topCampaigns[idx]?.campaign_name || topCampaigns[idx]?.campaign || '';
          },
          label: (ctx) => `Spend: ₹${Number(ctx.raw).toLocaleString('en-IN')}`,
        },
      },
    },
    scales: {
      x: {
        ticks: { callback: (v) => formatShortRupee(v) },
        grid: { color: '#f1f5f9' },
      },
      y: {
        grid: { display: false },
        ticks: { font: { size: 11.5 } },
      },
    },
  };

  return (
    <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
      <div id="content">
        {/* Top Header Row 1: Breadcrumbs on Left and Date Range & Actions in single line on Right */}
        <div className="row pb-2 align-items-center">
          <div className="col-md-4">
            <ul className="breadcrumb" style={{ margin: 0, padding: 0, background: 'transparent' }}>
              <li className="breadcrumb-item">
                <a href="/dashboard" style={{ textDecoration: 'none' }}>
                  <FontAwesomeIcon icon={faHome} /> Home
                </a>
              </li>
              <li className="breadcrumb-item">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(isCampaignMode ? '/Report/adsReport?view=campaign' : '/Report/adsReport');
                  }}
                  style={{ textDecoration: 'none', color: '#007bff' }}
                >
                  Ads Report {isCampaignMode ? '— Campaign Mapping' : ''}
                </a>
              </li>
              <li className="breadcrumb-item active">{currentTitle}</li>
            </ul>
          </div>

          {/* Top Right Actions & Date Range Filter - Single Row */}
          <div className="col-md-8 d-flex justify-content-end align-items-center gap-2">
            <button
              type="button"
              className="btn btn-default btn-sm"
              style={{ height: '31px' }}
              onClick={() => navigate(isCampaignMode ? '/Report/adsReport?view=campaign' : '/Report/adsReport')}
              title={isCampaignMode ? 'Back to Campaign Mapping' : 'Back to all platforms'}
            >
              <FontAwesomeIcon icon={faChevronLeft} className="me-1" />
              {isCampaignMode ? 'Back to Campaign Mapping' : 'Back to Platforms'}
            </button>

            {/* Quick Presets Dropdown */}
            <select
              className="form-control"
              style={{ width: '115px', height: '31px', padding: '2px 8px', fontSize: '12px' }}
              onChange={(e) => handleDatePreset(e.target.value)}
              defaultValue="30days"
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="thisMonth">This Month</option>
              <option value="all">All Time</option>
            </select>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                background: '#fff',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                padding: '3px 8px',
                gap: '6px',
                height: '31px',
              }}
            >
              <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#6c757d', fontSize: '12px' }} />
              <span style={{ fontSize: '11.5px', fontWeight: '600', color: '#495057' }}>From:</span>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value }))}
                style={{ border: 'none', outline: 'none', fontSize: '12px', background: 'transparent', padding: '0 2px' }}
              />
              <span style={{ fontSize: '11.5px', fontWeight: '600', color: '#495057' }}>To:</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
                style={{ border: 'none', outline: 'none', fontSize: '12px', background: 'transparent', padding: '0 2px' }}
              />
            </div>

            <button
              className="btn btn-default btn-sm"
              style={{ height: '31px' }}
              onClick={refreshAllData}
              title="Apply Date & Campaign Filter & Refresh"
            >
              <FontAwesomeIcon icon={faSync} spin={loading || tableLoading} />
            </button>
          </div>
        </div>

        {/* Row 2: Campaign Filter Dropdown */}
        <div className="row pb-3">
          <div className="col-12 d-flex justify-content-end align-items-center gap-2">
            <PermissionWrapper module="report_ads_recalculate" action="view">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                style={{
                  height: '31px',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: '600',
                }}
                onClick={handleRecalculate}
                disabled={recalculating}
                title="Recalculate / Sync Ads Report from Google Sheet"
              >
                <FontAwesomeIcon icon={faSync} spin={recalculating} />
                <span>{recalculating ? 'Recalculating...' : 'Recalculate'}</span>
              </button>
            </PermissionWrapper>

            <select
              className="form-control"
              style={{
                minWidth: '280px',
                maxWidth: '450px',
                height: '31px',
                padding: '2px 8px',
                fontSize: '12px',
                borderRadius: '4px',
                cursor: 'pointer',
                backgroundColor: '#fff',
                borderColor: columnFilters.campaign ? '#007bff' : '#ced4da',
                fontWeight: columnFilters.campaign ? '600' : '400',
              }}
              value={columnFilters.campaign || ''}
              onChange={(e) => {
                const updated = { ...columnFilters, campaign: e.target.value };
                setColumnFilters(updated);
                fetchTable(1, pageSize, orderBy, orderDir, updated);
              }}
            >
              <option value="">-- All Campaigns --</option>
              {filterOptions.campaigns?.map((c, idx) => {
                const val = typeof c === 'object' ? c.campaign : c;
                const label =
                  typeof c === 'object'
                    ? (c.label || (c.campaign_name ? `${c.campaign} - ${c.campaign_name}` : c.campaign))
                    : c;
                return (
                  <option key={idx} value={val}>
                    {label}
                  </option>
                );
              })}
            </select>

            {columnFilters.campaign && (
              <button
                className="btn btn-default btn-sm"
                style={{ height: '31px', padding: '2px 10px', fontSize: '12px' }}
                onClick={() => {
                  const updated = { ...columnFilters, campaign: '' };
                  setColumnFilters(updated);
                  fetchTable(1, pageSize, orderBy, orderDir, updated);
                }}
                title="Clear Campaign Filter"
              >
                <FontAwesomeIcon icon={faTimes} className="me-1 text-danger" /> Clear Filter
              </button>
            )}
          </div>
        </div>

        {/* Section 1: KPI Statistics Counters for this platform */}
        <AdsReportStats kpi={summaryData.kpi} loading={loading} />

        {/* Section 2: Visual Analytics Charts for this platform - 1 chart per row */}
        {/* Row 1: Spend & Leads Timeline */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="panel panel-default with-rounded-corner with-shadow chartbox" style={{ marginBottom: 0 }}>
              <div className="panel-heading">
                <h4 className="panel-title" style={{ fontSize: '13.5px', fontWeight: '600', margin: 0 }}>
                  <FontAwesomeIcon icon={faBullhorn} style={{ color: '#3b82f6', marginRight: 6 }} />
                  {currentTitle} Spend & Leads Timeline
                </h4>
              </div>
              <div className="panel-body" style={{ height: '300px', padding: '15px' }}>
                <Bar key="detail-trend-chart" data={trendChartData} options={trendChartOptions} />
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Top Campaigns by Spend */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="panel panel-default with-rounded-corner with-shadow chartbox" style={{ marginBottom: 0 }}>
              <div className="panel-heading">
                <h4 className="panel-title" style={{ fontSize: '13.5px', fontWeight: '600', margin: 0 }}>
                  Top {currentTitle} Campaigns by Spend
                </h4>
              </div>
              <div className="panel-body" style={{ height: '340px', padding: '15px' }}>
                <Bar key="detail-top-campaigns-chart" data={topCampaignsData} options={topCampaignsOptions} />
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: Action & Control Bar above Table */}
        <div
          className="panel-heading d-flex justify-content-between align-items-center"
          style={{ padding: '8px 0', borderBottom: 'none' }}
        >
          {/* Left: Table Title & Clear Filters */}
          <div className="d-flex align-items-center gap-2">
            <h4 className="panel-title" style={{ fontSize: '14px', fontWeight: '600', margin: 0 }}>
              {currentTitle} Campaign Records
            </h4>

            {hasAnyFilter() && (
              <button
                className="btn btn-default btn-xs"
                style={{ marginLeft: '10px' }}
                onClick={handleClearFilters}
                title="Clear all filters"
              >
                Clear Filter
              </button>
            )}
          </div>

          {/* Right: Record count, refresh, export, pagination */}
          <div className="d-flex align-items-center">
            <span style={{ fontSize: '0.8rem' }} className="me-3">
              {tableLoading ? (
                <span className="skeleton-text" style={{ width: '140px', display: 'inline-block' }}></span>
              ) : (
                showingRecords
              )}
            </span>

            <button className="btn btn-default btn-sm mx-1" onClick={refreshAllData} title="Refresh Data">
              <FontAwesomeIcon icon={faSync} />
            </button>

            <button
              className="btn btn-default btn-sm mx-1"
              onClick={handleExportCSV}
              disabled={exportLoading || totalRecords === 0}
              title="Export Report"
            >
              {exportLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Exporting...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faDownload} className="me-1" /> Export Report
                </>
              )}
            </button>

            <button
              className={`btn btn-default btn-sm ${currentPage === 1 ? 'd-none' : ''}`}
              title="Previous"
              onClick={() => fetchTable(currentPage - 1, pageSize, orderBy, orderDir, columnFilters)}
              disabled={currentPage === 1}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            &nbsp;
            <button
              className={`btn btn-default btn-sm ${currentPage >= totalPages ? 'd-none' : ''}`}
              title="Next"
              onClick={() => fetchTable(currentPage + 1, pageSize, orderBy, orderDir, columnFilters)}
              disabled={currentPage >= totalPages}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>

        {/* Section 4: Data Records Table for this platform */}
        <AdsReportTable
          data={tableData}
          page={currentPage}
          limit={pageSize}
          loading={tableLoading}
          orderBy={orderBy}
          orderDir={orderDir}
          columnFilters={{ ...columnFilters, platform }}
          filterOptions={filterOptions}
          onColumnFilterChange={(col, val) => {
            const updated = { ...columnFilters, [col]: val };
            setColumnFilters(updated);
            if (col === 'month' || col === 'platform') {
              fetchTable(1, pageSize, orderBy, orderDir, updated);
            }
          }}
          onApplyFilters={() => {
            fetchSummary();
            fetchTable(1, pageSize, orderBy, orderDir, columnFilters);
          }}
          onSortChange={(col, dir) => {
            setOrderBy(col);
            setOrderDir(dir);
            fetchTable(1, pageSize, col, dir, columnFilters);
          }}
        />
      </div>
    </div>
  );
};

export default AdsReportDetail;
